import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import 'firebase/database';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import Checkbox from 'material-ui/Checkbox';
import { Draggable } from 'react-beautiful-dnd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Loading from '../../../../components/Loading';
import { STATIC_HOMEPAGE_URL } from '../../../../config';
import Colors from '../../../../styles/colors';
import {
  createTimeDiff,
  extractRealTimePropsFromEpisode,
  getEpisodeFirebase,
  isEpisodeScheduled,
  getFormattedSchedule,
  getFormattedPublished,
  getTimeFromNow,
  getPublishedAtUnix,
} from '../../../../helpers/episodes';
import {
  EpisodeRow,
  EpisodeColumn,
  EpisodeRowTitle,
  CheckboxWrapper,
  AnalyticsIcon,
} from './styled';

// realtime props that contributes to when
// the channel will be open live
const realTimeProps = {
  episodeState: ({ episodeState = null }) => !isEqual(episodeState, 'ready'),
  publishOn: ({ publishOn = null }) => !!publishOn,
  audioProcessing: ({ audioProcessing = null }) => !!audioProcessing,
  isPublished: ({ isPublished = false, date_published = '', date_created = '' }) => {
    if (!isPublished) {
      return false;
    }

    // if published but the publishing diff is under 30mins, give it a chance
    // to listen and open the episode channel for the time being
    if (date_published && date_created) {
      const getDifference = createTimeDiff(date_created, date_published);
      return getDifference.asMinutes() <= 30;
    }

    return false;
  },
};

const EpisodeDraggable = props => {
  const [liveChannel, setLiveChannel] = useState(false);
  const [waitStatus, setWaitStatus] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    // handle episode live channel
    handleEpisodeLiveChannel();
  }, []);

  useEffect(() => {
    return () => {
      if (liveChannel) {
        handleEpisodeCloseChannel();
      }
    };
  }, [liveChannel]);

  const handleEpisodeLiveChannel = async () => {
    const { episode, onUpdateEpisode } = props;
    const {
      index,
      episodeId,
      published = false,
      activeChannel = false,
      partialOpenChannelOnPublish = false,
      localData = false,
    } = episode;

    // by default published episode no need to open channels
    // but published one sometimes need at least one occurence
    // by using the `partialOpenChannelOnPublish` flag on episode
    if (published && !partialOpenChannelOnPublish) {
      return false;
    }

    // get the episode realtime data first
    // only for episode that haven't published
    // and doesn't have an active channel
    const episodeFirebase = localData ? episode : await getEpisodeFirebase(episodeId);

    // if the episode is in draft and has no firebase data, its invalid
    if (!localData && isEmpty(episodeFirebase)) {
      // if the episode even have no audio url, just delete them from postgres
      if (isEmpty(episode.url)) {
        // onDelete(true);
        return setInvalid(true);
      }
    }

    // decide if we open the channel based from real time props
    const openChannelFromRealtimeProps = await new Promise(resolve => {
      if (isEmpty(episodeFirebase)) {
        return resolve(false);
      }

      // the props that will trigger the live channel to open
      // add more props here that can be use to open the channel
      // make sure the ones that can open the channel are functions
      const decision = Object.keys(realTimeProps)
        .filter(propkey => isFunction(realTimeProps[propkey]))
        .reduce((open, propkey) => {
          if (!open && propkey in episodeFirebase) {
            const willOpen = realTimeProps[propkey](episodeFirebase);
            open = !!willOpen;
          }

          return open;
        }, false);

      resolve(decision);
    });

    // if the episode from redux has `activeChannel` and not published then allow
    // or it was decided based on real time props conditions
    const willOpenChannel =
      activeChannel || partialOpenChannelOnPublish || openChannelFromRealtimeProps;

    setLiveChannel(willOpenChannel);
    setWaitStatus(willOpenChannel);

    // open episode live channel when set
    if (willOpenChannel) {
      return openEpisodeChannel(episodeId);
    }

    // if no open channel, just update the episode in redux (only unpublished)
    // with `localData` flag so we can stop updating it everytime it loads
    if (localData) {
      return false;
    }

    // get the metadata based from real time props (we had set)
    const metadata = extractRealTimePropsFromEpisode(episodeFirebase);
    console.log('initial metadata', episodeId, index, metadata);

    onUpdateEpisode({
      ...episode,
      ...metadata,
      localData: true,
    });
  };

  const handleEpisodeCloseChannel = () => {
    const {
      episode: { episodeId },
    } = props;

    closeEpisodeChannel(episodeId);
  };

  const openEpisodeChannel = episodeId => {
    const { episode, onUpdateEpisode } = props;
    const { published = false, partialOpenChannelOnPublish: oldPartialOpenChannel } = episode;

    console.log('opening episode channel', episodeId);
    firebase
      .database()
      .ref(`episodes/${episodeId}`)
      .on('value', snapshot => {
        if (!snapshot.exists()) {
          return false;
        }

        // get the metadata for this component (from firebase)
        const metadata = extractRealTimePropsFromEpisode(snapshot.val());
        console.log('got data from active channel', episodeId, metadata);

        // TODO: create an API to pull episode details and replace it (for processing and scheduled states)
        // partial opened channel are only active until `episodeState` become ready
        const { episodeState } = metadata;
        const partialOpenChannelOnPublish =
          published && isEqual(episodeState, 'ready') ? false : true;

        // if it was an activeChannel make sure to close it
        // as long as the partialchannel was opened before and in published state
        const toCloseChannel = oldPartialOpenChannel && !partialOpenChannelOnPublish;
        const activeChannel = !toCloseChannel;
        if (!activeChannel) {
          handleEpisodeCloseChannel();
        }

        setWaitStatus(false);
        setLiveChannel(activeChannel);
        onUpdateEpisode(
          {
            ...episode,
            ...metadata,
            activeChannel,
            partialOpenChannelOnPublish,
          },
          true
        );
      });
  };

  const closeEpisodeChannel = episodeId => {
    if (isEmpty(episodeId)) {
      return console.error('closeEpisodeChannel - empty episodeId');
    }
    console.log('closeEpisodeChannel', episodeId);
    firebase.database().ref(`episodes/${episodeId}`).off('value');
  };

  const hasErrorStatusState = () => {
    const { episode } = props;
    const { episodeState = '', url = '' } = episode;
    return isEmpty(url) || (episodeState || '').slice(0, 6) === 'Error:';
  };

  const getEpisodeStatus = () => {
    const { episode } = props;
    const {
      audioProcessing = false,
      published = false,
      isPublished = false,
      date_published,
      publishedAt = '',
      date_created,
      episodeState = '',
      publishOn,
      url = '',
    } = episode;

    // render invalid status
    if (invalid) {
      return <span title="data no longer exist">invalid</span>;
    }

    // if metadata is empty, wait for it before rendering status
    if (waitStatus) {
      return (
        <Loading
          type="circle"
          align="center"
          size={20}
          fontSize={1.5}
          color={Colors.mainOrage}
          fill={Colors.mainOrage}
        />
      );
    }

    // published is the new transitioned prop
    // isPublished is old prop coming from firebase
    const episodePublished = published || isPublished;

    // publishedAt is the new transitioned prop
    // date_published is old coming from firebase
    const episodePublishedUnix = getPublishedAtUnix(publishedAt) || date_published || date_created;

    // if there's an error on processing
    if (hasErrorStatusState()) {
      return isEmpty(url)
        ? 'audio file not loaded'
        : episodeState.slice(7, 32);
    }

    // audio processing status
    if (audioProcessing || episodeState === 'processing') {
      return 'processing';
    }

    if (episodeState === 'waiting') {
      return 'waiting';
    }

    if (isEpisodeScheduled({ publishOn })) {
      return (
        <React.Fragment>
          <div className="text-medium">scheduled on</div>
          <div className="font-weight-600">{getFormattedSchedule(publishOn)}</div>
        </React.Fragment>
      );
    }

    // episode is ready
    if (episodePublished) {
      return (
        <React.Fragment>
          <div className="text-medium">published on</div>
          <div className="font-weight-600" title={getTimeFromNow(episodePublishedUnix)}>
            {getFormattedPublished(episodePublishedUnix)}
          </div>
        </React.Fragment>
      );
    }

    return 'draft';
  };

  const {
    index,
    episode,
    checked,
    hasLanding,
    onCheck,
    onDelete,
    onEdit,
    onViewLanding,
    onViewAnalytics,
  } = props;

  console.log(`render episode-${index} ${episode.episodeId} ${episode.index}`);

  if (invalid) {
    return null;
  }

  return (
    <Draggable
      key={`episode-${episode.episodeId}`}
      draggableId={`episode-${episode.episodeId}`}
      index={index}>
      {({ innerRef, draggableProps, dragHandleProps }) => (
        <div
          ref={innerRef}
          {...draggableProps}
          {...dragHandleProps}
          style={{
            padding: '5px 15px',
            outline: 0,
            ...draggableProps.style,
          }}>
          <EpisodeRow alignItems="center">
            <EpisodeColumn className="col-md-7">
              <EpisodeRowTitle>
                <CheckboxWrapper width={25} className="checkbox-wrapper">
                  <Checkbox
                    checked={checked}
                    onCheck={onCheck}
                    labelStyle={{
                      fontSize: '1.6rem',
                      fontWeight: 'normal',
                      margin: 0,
                    }}
                    iconStyle={{
                      width: 25,
                      height: 25,
                      fill: '#ff8f53',
                      transition: 'none',
                    }}
                  />
                </CheckboxWrapper>
                <div className="episode-details">
                  <div style={{ marginTop: 0, cursor: 'move' }}>{episode.title}</div>
                  <div style={{ marginBottom: 5 }}>
                    <span
                      style={{
                        marginRight: 10,
                        cursor: 'pointer',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: '#F44336',
                      }}
                      onClick={onDelete}>
                      Delete
                    </span>
                    <span
                      style={{
                        marginRight: 10,
                        cursor: 'pointer',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: '#00BCD4',
                      }}
                      onClick={onEdit}>
                      Edit
                    </span>
                    {(hasLanding && (
                      <a
                        target="_blank"
                        href={`${STATIC_HOMEPAGE_URL}/episodes/${episode.episodeId}`}
                        onClick={onViewLanding}>
                        <span
                          className="text-dark-gray"
                          style={{
                            marginRight: 10,
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                          }}>
                          View landing page
                        </span>
                      </a>
                    )) ||
                      null}
                    {episode.publicEpisode && (
                      <CopyToClipboard
                        text={episode.url}
                        onCopy={() => alert('Audio file URL copied to clipboard.')}>
                        <span
                          style={{
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#f76b1c',
                          }}>
                          Copy audio file URL
                        </span>
                      </CopyToClipboard>
                    )}
                  </div>
                </div>
              </EpisodeRowTitle>
            </EpisodeColumn>
            <EpisodeColumn className="col-md-2 text-center" errored={hasErrorStatusState()}>
              {getEpisodeStatus()}
            </EpisodeColumn>
            <EpisodeColumn className="col-md-2 text-center">
              {(episode.duration && `${Math.round(episode.duration / 60)} minutes`) || '-'}
            </EpisodeColumn>
            <EpisodeColumn
              onClick={onViewAnalytics}
              className="col-md-1 text-center"
              datatoggle="tooltip"
              dataplacement="top"
              title="track analytics">
              <AnalyticsIcon className="fas fa-2x fa-chart-bar" />
            </EpisodeColumn>
          </EpisodeRow>
        </div>
      )}
    </Draggable>
  );
};

EpisodeDraggable.propTypes = {
  index: PropTypes.number,
  episode: PropTypes.object,
  checked: PropTypes.bool,
  onCheck: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onViewLanding: PropTypes.func,
  onViewAnalytics: PropTypes.func,
  onOpenChannel: PropTypes.func,
  onCloseChannel: PropTypes.func,
  onUpdateEpisode: PropTypes.func,
};

EpisodeDraggable.defaultProps = {
  index: 0,
  episode: {},
  checked: false,
  hasLanding: false,
  onCheck: () => {},
  onDelete: () => {},
  onEdit: () => {},
  onViewLanding: () => {},
  onViewAnalytics: () => {},
  onOpenChannel: () => {},
  onCloseChannel: () => {},
  onUpdateEpisode: () => {},
};

export default React.memo(EpisodeDraggable, (props, nextProps) => {
  const { episode: thisEpisode, checked: thisChecked } = props;
  const { episode: nextEpisode, checked: nextChecked } = nextProps;

  if (!isEqual(thisChecked, nextChecked)) {
    return false;
  }

  if (isEqual(thisEpisode, nextEpisode)) {
    return true;
  }

  return false;
});
